import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, ChevronLeft, ChevronRight, FileCheck2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const FCO_INCOTERMS = ['EXW', 'FOB', 'CIF', 'CFR', 'DAP'];
const INCOTERM_DESC = {
  EXW: 'Ex Works',
  FOB: 'Free On Board',
  CIF: 'Cost, Insurance & Freight',
  CFR: 'Cost & Freight',
  DAP: 'Delivered at Place',
};

/**
 * Seller FCO wizard — 3 steps (Commodity + Specs → Commercial → Review).
 * `onIssued(fco)` is called after a successful POST /deals/{id}/fco.
 */
export default function FCOWizard({ dealId, open, onOpenChange, onIssued }) {
  const { getAuthHeader } = useAuth();
  const [step, setStep] = useState(0);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});

  // Reset + reload config every time the modal opens
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setCfg(null);
    axios
      .get(`${API_URL}/deals/${dealId}/fco/config`, { headers: getAuthHeader() })
      .then((r) => {
        setCfg(r.data);
        const pre = r.data.prefill || {};
        // Seed spec inputs with empty values in the correct order
        const specs = {};
        (r.data.spec_fields || []).forEach((f) => {
          specs[f.key] = { value: '', rejection: '' };
        });
        const prefIncoterm = (pre.incoterm || 'CIF').toString().trim().split(' ')[0].toUpperCase();
        setForm({
          origin: pre.origin || '',
          quantity: pre.quantity || '',
          unit: pre.unit || 'tons',
          price_per_unit: pre.price_per_unit || '',
          currency: pre.currency || 'USD',
          incoterm: FCO_INCOTERMS.includes(prefIncoterm) ? prefIncoterm : 'CIF',
          loading_port: pre.loading_port || '',
          discharge_port: pre.discharge_port || '',
          payment_terms: pre.payment_terms || '',
          procedure: pre.procedure || '',
          specs,
        });
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'Failed to load FCO configuration');
        onOpenChange(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dealId]);

  // Memoised so the useMemo below has a stable dependency (fixes CRA
  // react-hooks/exhaustive-deps warning that fails Vercel's CI build).
  const specFields = useMemo(() => cfg?.spec_fields || [], [cfg]);

  const missingRequiredSpecs = useMemo(() => {
    if (!form.specs) return [];
    return specFields
      .filter((f) => f.required && !(form.specs[f.key]?.value ?? '').trim())
      .map((f) => f.label);
  }, [specFields, form.specs]);

  const canGoStep1 = () =>
    (form.origin || '').trim().length > 0 && missingRequiredSpecs.length === 0;
  const canGoStep2 = () =>
    Number(form.quantity) > 0 && Number(form.price_per_unit) > 0 && (form.incoterm || '').trim();

  const setSpec = (key, prop, v) =>
    setForm((f) => ({
      ...f,
      specs: { ...f.specs, [key]: { ...(f.specs[key] || {}), [prop]: v } },
    }));

  const totalValue =
    Number(form.quantity || 0) * Number(form.price_per_unit || 0) || 0;

  const submit = async () => {
    setBusy(true);
    try {
      // Whitelist the payload — backend rejects unknown fields.
      const specs = specFields
        .map((f) => ({
          key: f.key,
          label: f.label,
          unit: f.unit || '',
          value: (form.specs[f.key]?.value ?? '').trim(),
          rejection: (form.specs[f.key]?.rejection ?? '').trim(),
        }))
        .filter((s) => s.value.length > 0);
      const res = await axios.post(
        `${API_URL}/deals/${dealId}/fco`,
        {
          origin: form.origin,
          specs,
          quantity: Number(form.quantity),
          unit: form.unit,
          price_per_unit: Number(form.price_per_unit),
          currency: form.currency || 'USD',
          incoterm: form.incoterm,
          loading_port: form.loading_port,
          discharge_port: form.discharge_port,
          payment_terms: form.payment_terms,
          procedure: form.procedure,
        },
        { headers: getAuthHeader() },
      );
      toast.success(`FCO ${res.data.fco_number} issued`);
      onIssued?.(res.data);
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === 'string' ? detail : detail?.message || err.message || 'Failed to issue FCO';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[#121A2F] border-[#1E293B] text-white p-0"
                     data-testid="fco-wizard">
        <DialogHeader className="px-6 py-4 border-b border-[#1E293B]">
          <DialogTitle className="text-white flex items-center gap-2">
            <FileCheck2 className="w-5 h-5 text-[#F59E0B]" />
            Generate Full Corporate Offer
          </DialogTitle>
          <DialogDescription className="text-[#94A3B8] text-sm">
            {cfg
              ? `Step ${step + 1} of 3 · Valid for ${cfg.validity_days} days from issuance`
              : 'Loading listing defaults…'}
          </DialogDescription>
        </DialogHeader>

        {!cfg ? (
          <div className="p-10 text-center"><Loader2 className="w-8 h-8 animate-spin text-[#F59E0B] mx-auto" /></div>
        ) : (
          <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
            {step === 0 && (
              <>
                <div className="flex items-start gap-2 rounded-md border border-[#F59E0B]/25 bg-[#F59E0B]/[0.06] px-3 py-2 text-xs text-[#94A3B8]">
                  <ShieldCheck className="w-4 h-4 text-[#F59E0B] mt-0.5 flex-shrink-0" />
                  <span>Do not include phone numbers, emails or external URLs — all buyer communication happens on GlobalMinerX.</span>
                </div>
                <Field label="Commodity origin *" testid="fco-origin"
                       placeholder="e.g. Manyoni, Tanzania"
                       value={form.origin} onChange={(v) => setForm({ ...form, origin: v })} />
                <div>
                  <Label className="text-white text-sm mb-2 block">Commodity specifications</Label>
                  <div className="space-y-3">
                    {specFields.map((f) => (
                      <div key={f.key} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr] gap-3 items-end">
                        <div>
                          <Label className="text-[#94A3B8] text-xs">
                            {f.label}{f.required && <span className="text-[#F59E0B]"> *</span>}
                            {f.unit && <span className="text-[#64748B] ml-1">({f.unit})</span>}
                          </Label>
                          <Input value={form.specs?.[f.key]?.value ?? ''} onChange={(e) => setSpec(f.key, 'value', e.target.value)}
                                 placeholder={f.hint || 'Typical value'}
                                 data-testid={`fco-spec-${f.key}`}
                                 className="bg-[#0B1120] border-[#1E293B] text-white mt-1" />
                        </div>
                        <div>
                          <Label className="text-[#94A3B8] text-xs">Rejection threshold <span className="text-[#64748B]">(optional)</span></Label>
                          <Input value={form.specs?.[f.key]?.rejection ?? ''} onChange={(e) => setSpec(f.key, 'rejection', e.target.value)}
                                 placeholder={`e.g. below ${f.hint?.split(' ')[0] || 'X'}`}
                                 data-testid={`fco-rejection-${f.key}`}
                                 className="bg-[#0B1120] border-[#1E293B] text-white mt-1" />
                        </div>
                      </div>
                    ))}
                  </div>
                  {missingRequiredSpecs.length > 0 && (
                    <p className="text-xs text-[#EF4444] mt-2">Required: {missingRequiredSpecs.join(', ')}</p>
                  )}
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Quantity *" testid="fco-quantity" type="number" step="1"
                         value={form.quantity} onChange={(v) => setForm({ ...form, quantity: v })} />
                  <Field label="Unit *" testid="fco-unit"
                         value={form.unit} onChange={(v) => setForm({ ...form, unit: v })}
                         placeholder="tons / kg / carats" />
                  <Field label="Price per unit *" testid="fco-price" type="number" step="0.01"
                         value={form.price_per_unit} onChange={(v) => setForm({ ...form, price_per_unit: v })} />
                  <Field label="Currency" testid="fco-currency"
                         value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} />
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Incoterm *</Label>
                    <select value={form.incoterm || 'CIF'}
                            onChange={(e) => setForm({ ...form, incoterm: e.target.value })}
                            data-testid="fco-incoterm"
                            className="w-full mt-1 h-10 rounded-md border border-[#1E293B] bg-[#0B1120] text-white px-3 text-sm">
                      {FCO_INCOTERMS.map((i) => (
                        <option key={i} value={i}>{i} — {INCOTERM_DESC[i]}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Total value</Label>
                    <div className="mt-1 h-10 flex items-center px-3 rounded-md border border-[#1E293B] bg-[#0B1120] text-[#F59E0B] font-semibold"
                         data-testid="fco-total-value">
                      {form.currency || 'USD'} {totalValue.toLocaleString()}
                    </div>
                  </div>
                  <Field label="Loading port" testid="fco-loading"
                         value={form.loading_port} onChange={(v) => setForm({ ...form, loading_port: v })} />
                  <Field label="Discharge port" testid="fco-discharge"
                         value={form.discharge_port} onChange={(v) => setForm({ ...form, discharge_port: v })} />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Payment terms</Label>
                  <Textarea rows={2} value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                            data-testid="fco-payment-terms"
                            className="bg-[#0B1120] border-[#1E293B] text-white mt-1" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Transaction procedure</Label>
                  <Textarea rows={5} value={form.procedure} onChange={(e) => setForm({ ...form, procedure: e.target.value })}
                            data-testid="fco-procedure"
                            className="bg-[#0B1120] border-[#1E293B] text-white mt-1" />
                </div>
              </>
            )}

            {step === 2 && (
              <div className="space-y-4 text-sm">
                <ReviewRow k="Commodity" v={cfg.prefill?.commodity_name || '—'} />
                <ReviewRow k="Origin" v={form.origin} />
                <ReviewRow k="Quantity" v={`${form.quantity} ${form.unit}`} />
                <ReviewRow k="Price" v={`${form.currency} ${Number(form.price_per_unit).toLocaleString()} / ${form.unit}`} />
                <ReviewRow k="Total" v={`${form.currency} ${totalValue.toLocaleString()}`} highlight />
                <ReviewRow k="Incoterm" v={form.incoterm} />
                <ReviewRow k="Loading / Discharge" v={`${form.loading_port || '—'} → ${form.discharge_port || '—'}`} />
                <ReviewRow k="Payment" v={form.payment_terms} multiline />
                <ReviewRow k="Specifications" v={
                  specFields
                    .filter((f) => (form.specs[f.key]?.value ?? '').trim())
                    .map((f) => `${f.label}: ${form.specs[f.key].value}${f.unit ? ' ' + f.unit : ''}`)
                    .join(' · ') || '—'
                } multiline />
                <p className="text-xs text-[#64748B]">Once issued, the buyer has {cfg.validity_days} days to acknowledge or reject.</p>
              </div>
            )}
          </div>
        )}

        <div className="border-t border-[#1E293B] px-6 py-3 flex justify-between items-center gap-3">
          <Button variant="outline" disabled={step === 0 || busy}
                  onClick={() => setStep(step - 1)}
                  data-testid="fco-back-btn"
                  className="bg-transparent border-[#1E293B] text-white">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {step < 2 && (
            <Button
              disabled={busy || (step === 0 ? !canGoStep1() : !canGoStep2())}
              onClick={() => setStep(step + 1)}
              data-testid="fco-next-btn"
              className="bg-[#F59E0B] text-[#0B1120] hover:bg-[#D97706] font-semibold">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          {step === 2 && (
            <Button disabled={busy} onClick={submit}
                    data-testid="fco-submit-btn"
                    className="bg-[#F59E0B] text-[#0B1120] hover:bg-[#D97706] font-semibold">
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Issuing…</> : 'Issue FCO'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, testid, value, onChange, type = 'text', step, placeholder }) => (
  <div>
    <Label className="text-[#94A3B8] text-xs">{label}</Label>
    <Input type={type} step={step} placeholder={placeholder}
           value={value ?? ''} onChange={(e) => onChange(e.target.value)}
           data-testid={testid}
           className="bg-[#0B1120] border-[#1E293B] text-white mt-1" />
  </div>
);

const ReviewRow = ({ k, v, highlight, multiline }) => (
  <div className={`flex ${multiline ? 'flex-col gap-1' : 'items-start justify-between gap-4'} border-b border-[#1E293B] pb-2`}>
    <span className="text-[#94A3B8] text-xs uppercase tracking-wider">{k}</span>
    <span className={`${highlight ? 'text-[#F59E0B] font-semibold' : 'text-white'} text-sm whitespace-pre-wrap`}>{v}</span>
  </div>
);
